import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Header from "../../components/Header";
import BackButton from "../../components/BackButton";
import UploadButton from "../../components/UploadButton";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";
import {
  createWorkoutCategory,
  createWorkoutProgram,
} from "../../services/programService";
import { uploadFile } from "../../services/exerciseService";

const ProgramValidationSchema = Yup.object().shape({
  type: Yup.string().required("Selection is required"),
  title: Yup.string()
    .required("Name is required")
    .matches(/^(?!\s*$).+/, "Name cannot be empty or just spaces"),
  description: Yup.string()
    .required("Description is required.")
    .max(1007, "Description cannot exceed 1000 characters.")
    .matches(/^(?!\s*$).+/, "Description cannot be empty or just spaces"),
  imageUrl: Yup.string().required("Image is required"),
});

interface ProgramFormValues {
  type: string;
  title: string;
  description: string;
  imageUrl?: string | null;
  imageUrlRectangle?: string | null;
}

const AddProgram: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [, setImagePreview] = useState<string | null>(null);
  const [, setCharCount] = useState(0);

  const handleImageUpload = async (
    file: File | null,
    setFieldValue: (field: string, value: any) => void
  ): Promise<string | null> => {
    if (!file) {
      return null;
    }

    try {
      const uploadedImage = await uploadFile(file);

      if (uploadedImage?.url) {
        setFieldValue("imageUrl", uploadedImage.url);
        setImagePreview(uploadedImage.url);
        return uploadedImage.url;
      } else {
        setError("Failed to upload image");
        return null;
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setError("Failed to upload image");
      return null;
    }
  };

  const handleRemoveImage = (
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("imageUrl", null);
    setImagePreview(null);
  };

  const initialValues: ProgramFormValues = {
    type: "",
    title: "",
    description: "",
    imageUrl: null,
    imageUrlRectangle: null,
  };

  const handleSubmit = async (values: ProgramFormValues) => {
    console.log(values);

    try {
      const payload: any = {
        title: values.title,
        description: values.description,
        imageUrl: values.imageUrl || "",
      };

      if (values.type === "Program") {
        payload.trainer = "";
        payload.trainerImage = null;
        await createWorkoutProgram(payload);
      } else if (values.type === "Workout Category") {
        payload.imageUrlRectangle = values.imageUrlRectangle || "";
        await createWorkoutCategory(payload);
      }

      setSuccessMessage(`${values.type} added successfully!`);
      setTimeout(() => {
        navigate("/programs");
      }, 2000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : `Failed to add ${values.type}`
      );
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen flex flex-col">
      <Header title="Add Program & Category" />
      <div className="w-full max-w-lg bg-white shadow-md rounded-md p-6">
        {error && (
          <ErrorMessage message={error} onClose={() => setError(null)} />
        )}
        {successMessage && (
          <SuccessMessage
            message={successMessage}
            onClose={() => setSuccessMessage(null)}
          />
        )}

        <div className="flex items-center justify-between mt-4 border-b pb-4">
          <BackButton onClick={() => navigate("/programs")} />
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={ProgramValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, values, dirty }) => (
            <Form>
              <div className="mb-4">
                <label
                  htmlFor="type"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Type
                </label>
                <Field
                  as="select"
                  id="type"
                  name="type"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                >
                  <option value="">Select type</option>
                  <option value="Workout Category">Workout Category</option>
                  <option value="Program">Program</option>
                </Field>
                {errors.type && touched.type && (
                  <div className="text-red-500 text-xs mt-1">{errors.type}</div>
                )}
              </div>

              {values.type && (
                <>
                  <div className="mb-4">
                    <label
                      htmlFor="title"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      {values.type === "Workout Category"
                        ? "Workout Category Name"
                        : "Program Name"}
                    </label>
                    <Field
                      id="title"
                      name="title"
                      type="text"
                      maxLength={100}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                      placeholder={`Enter ${values.type.toLowerCase()} name`}
                    />
                    {errors.title && touched.title && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.title}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="description"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      Description
                    </label>
                    <ReactQuill
                      value={values.description}
                      onChange={(content) => {
                        setFieldValue("description", content);
                        const textOnly = DOMPurify.sanitize(content, {
                          ALLOWED_TAGS: [],
                        }).trim();
                        setCharCount(textOnly.length);
                      }}
                      className="bg-white border border-gray-300 rounded-md shadow-sm"
                    />
                    {errors.description && touched.description && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.description}
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="mb-10">
                <label className="block text-gray-700 font-medium mb-2">
                  Upload Image
                </label>
                <div className="flex flex-row justify-evenly">
                  <div className="w-26 flex flex-col justify-center items-center">
                    {values.type === "Workout Category" && (
                      <label>Square Image</label>
                    )}
                    <UploadButton
                      label="Upload image"
                      onUpload={(file) =>
                        handleImageUpload(file, setFieldValue)
                      }
                      onRemove={() => handleRemoveImage(setFieldValue)}
                      accept="image/*"
                      index={0}
                    />
                    {errors.imageUrl && touched.imageUrl && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.imageUrl}
                      </div>
                    )}
                  </div>
                  {values.type === "Workout Category" && (
                    <div className="w-26 flex flex-col justify-center items-center">
                      <label>Rectangle Image</label>
                      <UploadButton
                        label="Upload image"
                        onUpload={(file) =>
                          handleImageUpload(file, (field: string, value: any) =>
                            setFieldValue("imageUrlRectangle", value)
                          )
                        }
                        onRemove={() =>
                          setFieldValue("imageUrlRectangle", null)
                        }
                        accept="image/*"
                        index={0}
                      />
                      {errors.imageUrlRectangle &&
                        touched.imageUrlRectangle && (
                          <div className="text-red-500 text-xs mt-1">
                            {errors.imageUrlRectangle}
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                className={`w-full py-2 text-white font-semibold rounded-md transition ${
                  dirty
                    ? "bg-yellow-600 hover:bg-yellow-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
                disabled={!dirty}
              >
                Add {values.type}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddProgram;
